import { useState, useEffect, VFC } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosResponse } from "axios";
import { Grid, Box, Button, SelectChangeEvent, Typography } from "@mui/material";

import { FormText } from "../common/form/FormText";
import { PullDownItem, FormPullDown } from "../common/form/FormPullDown";
import ButtonArea from "../common/ButtonArea";
import { ResultResponse, ResultDialog } from "../common/dialog/ResultDialog";
import { TenantService } from "./api/TenantService";
import { TenantSearchRequest, TenantSearchResponse } from "./api/TenantForm";
import Constants from "../../utils/Constants";
import { Utility } from "../../utils/Utility";
import { ApplicationProtectedRouting } from "../../Routing";
import { TenantTable } from "../common/table/TenantTable";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import TenantEditMode from "../../entity/TenantEditMode";
import { TENANT_STATUS, UPPER_OPERATION } from "../../entity/Tenant";
import { Loading } from "../common/Loading";

/**
 * テナント一覧画面
 */
const TenantList: VFC = () => {
  const navigate = useNavigate();
  const PAGE_NAME = "テナント一覧";
  const WIDTH = 2;
  // テナントステータスの選択肢
  const tenantStatusOptions = Utility.getTenantStatusOption();
  // 契約日の検索タイプ選択肢
  const tenantContractDateOption = Utility.getTenantContractDateOption();
  // 削除日の検索タイプ選択肢
  const isDeleteOption = Utility.getDeleteOption();
  // 上位操作の選択肢
  const upperOperations = Utility.getUpperOperationOption();

  /***************************************************************
   * State管理
   **************************************************************/
  // 共通ローディング
  const [isLoading, setIsLoading] = useState(false);

  // 検索条件
  const [request, setRequest] = useState<TenantSearchRequest>(
    new TenantSearchRequest()
  );

  // テナント一覧
  const [tenantList, setTenantList] = useState([] as TenantSearchResponse[]);

  // 共通ダイアログ(ステータス)
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // 処理結果
  const [resultResponse, setResultResponse] = useState<ResultResponse>({
    status: "",
    code: "",
    displayMessage: "",
    result: [],
    errorMessages: [],
  });

  /***************************************************************
   * 共通処理
   **************************************************************/
  // 処理結果ダイアログ(CLOSE)
  const handleResultDialogClose = () => {
    setIsDialogOpen(false);
  };
  // 処理結果を設定
  const setStateResultResponse = (
    status: string,
    code: string,
    displayMessage: string,
    result: string
  ) => {
    setIsDialogOpen(true);
    setResultResponse({
      status,
      code,
      displayMessage,
      result: [result],
      errorMessages: [],
    });
  };

  /***************************************************************
   * 初期表示処理
   **************************************************************/
  useEffect(() => {
    // セッション破棄
    sessionStorage.removeItem("TENANT_ID");

    // 検索処理
    searchButtonClick();
  }, []);

  /***************************************************************
   * 検索処理
   **************************************************************/
  // 入力変更
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRequest({ ...request, [e.target.name]: e.target.value });
  };
  const handleSelectChange = (e: SelectChangeEvent) => {
    setRequest({ ...request, [e.target.name]: e.target.value });
  };
  const handleDateChange = (name: string, date: Date | null) => {
    setRequest({ ...request, [name]: date ? date.toLocaleString() : null });
  };
  // 検索実行(Enter)
  const handleInputSearch = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      searchButtonClick();
    }
  };

  // 検索ボタン押下
  const searchButtonClick = () => {
    setIsLoading(true);
    // テナント一覧取得API;
    const service = new TenantService();
    service
      .findByConditions(request)
      .then((response: AxiosResponse) => {
        if (Constants.ERR_STATUS_OK === response.data.status) {
          // OK
          const responseList = response.data.data as TenantSearchResponse[];
          responseList.map(tenant => {
            const tenantStatusId = +tenant.tenantStatus;
            if (!Number.isNaN(tenantStatusId)) {
              tenant.tenantStatus = TENANT_STATUS[tenantStatusId];
            }
            const upperOperationId = +tenant.upperOperation;
            if (!Number.isNaN(upperOperationId)) {
              tenant.upperOperation = UPPER_OPERATION[upperOperationId];
            }
            tenant.parentName = Utility.NullEmptyStringWrap(tenant.parentName);
          })
          setTenantList(response.data.data);
        } else {
          setIsDialogOpen(true);
          setResultResponse(response.data);
        }
      })
      .catch((err) => {
        setStateResultResponse(
          Constants.ERR_STATUS_NG,
          err.response.status,
          err.response.statusText,
          Constants.E_MSG_SYSTEM_ERR
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /***************************************************************
   * 新規追加
   **************************************************************/
  // 新規追加ボタン押下
  const newButtonClick = () => {
    // ユーザー編集/新規作成画面へ遷移する
    navigate(ApplicationProtectedRouting.tenantCreate.path(), {state: {
      mode: TenantEditMode.CREATE
    }});
  };

  /***************************************************************
   * レイアウト
   **************************************************************/

  // 検索フォーム
  const SearchForm = () => {
    return (
      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* 検索条件：公開コード */}
          <FormText
            label="テナントコード"
            name="disclosureCode"
            xs={WIDTH}
            handle={handleInputChange}
            handleKeyDown={handleInputSearch}
          />

          {/* 検索条件：テナント名 */}
          <FormText
            label="テナント名"
            name="tenantName"
            xs={WIDTH}
            handle={handleInputChange}
            handleKeyDown={handleInputSearch}
          />

          {/* 検索条件：企業名 */}
          <FormText
            label="企業名"
            name="companyName"
            xs={WIDTH}
            handle={handleInputChange}
            handleKeyDown={handleInputSearch}
          />

          {/* 検索条件：親テナント名 */}
          <FormText
            label="親テナント名１"
            name="parentName"
            xs={WIDTH}
            handle={handleInputChange}
            handleKeyDown={handleInputSearch}
          />

          {/* 検索条件：上位操作 */}
          <FormPullDown
            label="上位からの操作"
            name="upperOperation"
            emptyItemLabel="（未指定）"
            isDisplayEmpty
            items={upperOperations}
            xs={WIDTH}
            handle={handleSelectChange}
          />

          {/* 検索条件：営業担当者名 */}
          <FormText
            label="営業担当者名"
            name="salesRepresentativeName"
            xs={WIDTH}
            handle={handleInputChange}
            handleKeyDown={handleInputSearch}
          />

          {/* 検索条件：テナントステータス */}
          <FormPullDown
            label="ステータス"
            name="tenantStatus"
            emptyItemLabel="（未指定）"
            isDisplayEmpty
            items={tenantStatusOptions}
            xs={WIDTH}
            handle={handleSelectChange}
          />

          {/* 検索条件： 契約日の検索条件オプション */}
          <FormPullDown
            label="日付検索タイプ"
            name="tenantContractDateOption"
            items={tenantContractDateOption}
            xs={WIDTH}
            handle={handleSelectChange}
            value={request.tenantContractDateOption}
          />

          {/* 検索条件： 契約日 */}
          <Grid item xs={WIDTH}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ja}
              dateFormats={{ monthAndYear: "yyyy年 MM月" }}
              localeText={{
                previousMonth: "前月を表示", // < のツールチップ
                nextMonth: "次月を表示", // > のツールチップ
              }}
            >
              <DatePicker
                slotProps={{
                  textField: {
                    size: 'small',
                    InputLabelProps: { shrink: true },
                    fullWidth: true,
                  },
                }}
                onChange={(date: Date | null) => {
                  handleDateChange("tenantContractDate", date);
                }}
              />
            </LocalizationProvider>
          </Grid> 

          {/* 検索条件： 削除日の検索条件オプション */}
          <FormPullDown
            label="削除"
            name="isDeleted"
            emptyItemLabel="（未指定）"
            isDisplayEmpty
            items={isDeleteOption}
            xs={WIDTH}
            handle={handleSelectChange}
            value={request.isDeleted?.toString()}
          />

          {/* 検索ボタン */}
          <Grid item xs={1}>
            <Button 
              variant="contained" onClick={searchButtonClick}>
              検索
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <>
      <Box>
        {/* ボタンエリア */}
        <ButtonArea pageName={PAGE_NAME}>
          <Button variant="contained" onClick={newButtonClick}>
            新規追加
          </Button>
        </ButtonArea>
        <Box sx={{ pt: 4 }}>
          {/* 検索フォーム */}
          {SearchForm()}
          {/* テナント一覧 */}
          {tenantList.length > 0 ? <>
            <TenantTable tenantList={tenantList} />
          </> : <>
            <Box>
              <Grid container alignItems={'center'} justifyContent={'center'}>
                {/* テナント情報なし */}
                <Grid item xs={12}  padding={3}>
                  <Typography variant="h2" align="center" fontSize={"1.25rem"} noWrap>
                    テナント情報なし
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </>}
        </Box>
      </Box>

      {/* 処理結果ダイアログ */}
      <ResultDialog
        isDialogOpen={isDialogOpen}
        resultResponse={resultResponse}
        handler={handleResultDialogClose}
      />

      {/* 処理ローディング */}
      <Loading isLoading={isLoading} />
    </>
  );
};

export default TenantList;
